import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Metadata from '../components/Metadata';
import PageHero from '../components/PageHero';
import SectionHeading from '../components/SectionHeading';
import ImageGallery from '../components/ImageGallery';
import useCmsCollection from '../hooks/useCmsCollection';
import { assets, galleryFilters, pageMeta } from '../data/siteData';
import { normalizeGalleryItems } from '../lib/gallery';
import { assetUrl } from '../lib/media';

export default function GalleryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter = searchParams.get('kategori') || '';

  const galleryQuery = useCmsCollection('galleri', {
    endpoint: 'v2',
    limit: 24,
    offset: 0,
    sort: 'updatedAt',
    order: 'desc',
    term: activeFilter ? [`kategori:${activeFilter}`] : [],
  });

  const galleries = normalizeGalleryItems(galleryQuery.items);

  const filterLabel = useMemo(() => {
    return galleryFilters.find((filter) => filter.value === activeFilter)?.label || 'Alle projekter';
  }, [activeFilter]);

  return (
    <>
      <Metadata
        title={pageMeta.gallery.title}
        description={pageMeta.gallery.description}
        image={assetUrl(pageMeta.gallery.ogImage, 1200)}
        path="/galleri"
      />

      <PageHero
        eyebrow="Galleri"
        title="Et samlet overblik over RS Bygs udførte projekter"
        description="Brug filtrene til at skifte mellem træterrasser, renovering, tagudskiftning samt vinduer og døre. Billederne åbnes i stor visning og er nemme at bruge på både mobil og desktop."
        image={assets.terrace}
        primaryAction={{ label: 'Kontakt os', to: '/kontakt' }}
        secondaryAction={{ label: 'Se ydelser', to: '/ydelser' }}
      />

      <section className="border-b border-brand-gray-200 bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Filtrering"
            title="Vælg den kategori, du vil se"
            description={`Aktuelt filter: ${filterLabel}. Filtreringen kører direkte på CMS-terms, så du altid ser det rette projektudvalg.`}
          />

          <div className="mt-10 flex flex-wrap gap-3">
            {galleryFilters.map((filter) => {
              const active = filter.value === activeFilter;
              return (
                <button
                  key={filter.value || 'all'}
                  type="button"
                  onClick={() => {
                    if (!filter.value) {
                      setSearchParams({});
                      return;
                    }
                    setSearchParams({ kategori: filter.value });
                  }}
                  className={`inline-flex min-h-11 items-center justify-center border px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] transition-colors ${active ? 'border-brand-black bg-brand-black text-white' : 'border-brand-gray-200 bg-white text-brand-black hover:border-brand-orange hover:text-brand-orange'}`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>

          <div className="mt-10">
            {galleryQuery.loading ? (
              <div className="border border-brand-gray-200 bg-brand-gray-50 p-6 text-sm text-brand-gray-600">
                Henter galleri...
              </div>
            ) : galleries.length ? (
              <ImageGallery items={galleries} columnsClass="lg:grid-cols-3" />
            ) : (
              <div className="border border-brand-gray-200 bg-brand-gray-50 p-8 text-sm text-brand-gray-600">
                Der er ingen projekter i denne kategori endnu.
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
